import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Snackbar,
  Alert,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const defaultForm = {
  name: '',
  description: '',
  price: '',
  display_price: '',
  duration_days: 30,
  currency: 'NGN',
  billing_label: '',
  button_text: 'Subscribe',
  badge_type: 'none',
  theme_color: 'blue',
  is_active: 1,
  features: '',
};

export default function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/admin/subscription-plans`);
      setPlans(res.data?.data || []);
    } catch (_err) {
      showSnackbar('Failed to load subscription plans', 'error');
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price || 0,
      display_price: plan.display_price || '',
      duration_days: plan.duration_days || 30,
      currency: plan.currency || 'NGN',
      billing_label: plan.billing_label || '',
      button_text: plan.button_text || 'Subscribe',
      badge_type: plan.badge_type || 'none',
      theme_color: plan.theme_color || 'blue',
      is_active: Number(plan.is_active) ? 1 : 0,
      features: (() => {
        if (Array.isArray(plan.features)) return plan.features.join('\n');
        if (typeof plan.features === 'string') {
          try {
            const parsed = JSON.parse(plan.features);
            return Array.isArray(parsed) ? parsed.join('\n') : plan.features;
          } catch {
            return plan.features;
          }
        }
        return '';
      })(),
    });
    setOpen(true);
  };

  const setFreePlanTemplate = () => {
    setEditing(null);
    setForm({
      name: 'Free',
      description: 'Access PeerMate for 1 hour every 48 hours. Upgrade for unlimited access.',
      price: 0,
      display_price: 'Free',
      duration_days: 30,
      currency: 'NGN',
      billing_label: 'Starter',
      button_text: 'Start Free',
      badge_type: 'recommended',
      theme_color: 'green',
      is_active: 1,
      features: [
        '1 hour PeerMate learning every 48 hours',
        'Basic personalized course suggestions',
        'Upgrade anytime for unlimited learning',
      ].join('\n'),
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (!form.name || !form.duration_days) {
        showSnackbar('Name and duration are required', 'error');
        return;
      }

      const featureList = String(form.features || '')
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);

      const payload = {
        ...form,
        features: featureList,
      };

      if (editing) {
        await axios.put(`${BASE_URL}/api/admin/subscription-plans/${editing.id}`, payload);
        showSnackbar('Subscription plan updated');
      } else {
        await axios.post(`${BASE_URL}/api/admin/subscription-plans`, payload);
        showSnackbar('Subscription plan created');
      }

      setOpen(false);
      fetchPlans();
    } catch (_err) {
      showSnackbar('Failed to save subscription plan', 'error');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/admin/subscription-plans/${id}`);
      showSnackbar('Subscription plan deleted');
      fetchPlans();
    } catch (_err) {
      showSnackbar('Failed to delete plan', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Subscription Plans</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={setFreePlanTemplate}>Add Free Plan</Button>
          <Button variant="contained" onClick={openCreate}>Add Plan</Button>
        </Stack>
      </Box>

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Display</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Badge / Theme</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>{plan.currency} {Number(plan.price || 0).toLocaleString()}</TableCell>
                  <TableCell>{plan.display_price || '-'}</TableCell>
                  <TableCell>{plan.duration_days} days</TableCell>
                  <TableCell>
                    {(plan.badge_type || 'none')} / {(plan.theme_color || 'blue')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={Number(plan.is_active) ? 'Active' : 'Inactive'}
                      color={Number(plan.is_active) ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => openEdit(plan)}>Edit</Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => remove(plan.id)}>Delete</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Update Plan' : 'Create Plan'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Plan Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            <TextField label="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} multiline rows={3} />
            <TextField label="Price" type="number" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: Number(e.target.value) }))} />
            <TextField label="Display Price" value={form.display_price} onChange={(e) => setForm((p) => ({ ...p, display_price: e.target.value }))} placeholder="e.g. Free or NGN 12,000" />
            <TextField label="Duration (days)" type="number" value={form.duration_days} onChange={(e) => setForm((p) => ({ ...p, duration_days: Number(e.target.value) }))} />
            <TextField label="Currency" value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))} />
            <TextField label="Billing Label" value={form.billing_label} onChange={(e) => setForm((p) => ({ ...p, billing_label: e.target.value }))} placeholder="e.g. per month" />
            <TextField label="Button Text" value={form.button_text} onChange={(e) => setForm((p) => ({ ...p, button_text: e.target.value }))} placeholder="Subscribe" />
            <TextField
              select
              label="Badge Type"
              value={form.badge_type}
              onChange={(e) => setForm((p) => ({ ...p, badge_type: e.target.value }))}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="popular">Popular</MenuItem>
              <MenuItem value="recommended">Recommended</MenuItem>
            </TextField>
            <TextField
              select
              label="Theme Color"
              value={form.theme_color}
              onChange={(e) => setForm((p) => ({ ...p, theme_color: e.target.value }))}
            >
              <MenuItem value="blue">Blue</MenuItem>
              <MenuItem value="green">Green</MenuItem>
              <MenuItem value="orange">Orange</MenuItem>
              <MenuItem value="purple">Purple</MenuItem>
              <MenuItem value="red">Red</MenuItem>
              <MenuItem value="slate">Slate</MenuItem>
            </TextField>
            <TextField
              label="Features (one per line)"
              value={form.features}
              onChange={(e) => setForm((p) => ({ ...p, features: e.target.value }))}
              multiline
              rows={5}
            />
            <TextField
              select
              label="Status"
              value={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: Number(e.target.value) }))}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.is_active) === 1}
                  onChange={(_, checked) => setForm((p) => ({ ...p, is_active: checked ? 1 : 0 }))}
                />
              }
              label="Plan is active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
